import {
  iconCabinet,
  iconFloor,
  iconGarage,
  iconPaint,
  iconRoofline,
  iconWall,
  iconWindow,
  pictogramCabinets,
  pictogramFlooring,
  pictogramGarageDoors,
  pictogramPaint,
  pictogramRoofing,
  pictogramWalls,
  pictogramWindows,
} from '@hover/icons';
import type { IconProp, PictogramProp } from '@hover/icons';
import { includes } from '@hover/utilities';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import type { ExtractRouteParams } from 'react-router';
import type { InferType } from 'yup';
import { array, number, object, string } from 'yup';

import type { SubRegion } from 'src/modules/homeownerApp/components/Customize/SubRegions';
import { useAvailableMaterialsRegions } from 'src/modules/homeownerApp/components/Palette';
import {
  usePartnerCobranding,
  usePartnerCustomizeCategories,
} from 'src/modules/homeownerApp/hooks/usePartner';
import type { Aspect } from 'src/modules/homeownerApp/types';
import type {
  AiDisplayImageFragment,
  AiGeneratedImageRegionsFragment,
} from 'src/shared/graphql/generated';
import { useAiGeneratedImageRegionsQuery } from 'src/shared/graphql/generated';
import type { Route } from 'src/shared/route';

import {
  type CustomizationCategory,
  type MaterialRegion,
  type PaintRegion,
  exteriorMaterialRegions,
  exteriorPaintRegions,
  interiorMaterialRegions,
  interiorPaintRegions,
} from '../../constants/customizationCategories';
import { getImageAspect } from '../../helpers';

const ENABLED_REGION_THRESHOLD = 0.01;

const imageRegionsSchema = array(
  object({
    region: string().required(),
    percentage: number().required().min(0).max(100),
  }),
).required();

type ImageRegions = InferType<typeof imageRegionsSchema>;
type ImageRegion = ImageRegions[number];
type ImageRegionMap = Record<ImageRegion['region'], ImageRegion['percentage']>;

export const parseImageRegions = (regions: Record<string, unknown>[]) =>
  imageRegionsSchema.isValidSync(regions) ? regions : [];

const regionMap = (regions: ImageRegions) => {
  const detectedRegions: ImageRegionMap = {};
  regions.forEach(region => {
    detectedRegions[region.region] = region.percentage;
  });
  return detectedRegions;
};

export interface MenuItem {
  category: CustomizationCategory;
  disabled?: boolean;
  icon: IconProp;
  label: string;
  pictogram?: PictogramProp;
}

export const paintRegionsForImage = (
  image: AiDisplayImageFragment,
): readonly PaintRegion[] =>
  getImageAspect(image) === 'exterior'
    ? exteriorPaintRegions
    : interiorPaintRegions;

export const materialRegionsForImage = (
  image: AiDisplayImageFragment,
): readonly MaterialRegion[] =>
  getImageAspect(image) === 'exterior'
    ? exteriorMaterialRegions
    : interiorMaterialRegions;

export const routeParamsToCategory = (
  image: AiDisplayImageFragment,
  slug: string | undefined,
  regionSlug: string | undefined,
): CustomizationCategory | null => {
  if (slug === 'paint' && includes(paintRegionsForImage(image), regionSlug)) {
    return { type: 'paint', region: regionSlug };
  }

  if (includes(materialRegionsForImage(image), slug))
    return { type: 'material', region: slug };

  return null;
};

export type MenuRouteParams = Omit<
  ExtractRouteParams<typeof Route.HOMEOWNER_APP_CUSTOMIZE.path>,
  'aiDisplayImageId'
>;

export const categoryToRouteParams = (
  category: CustomizationCategory,
): MenuRouteParams => {
  if (category.type === 'paint')
    return {
      category: 'paint',
      region: category.region,
    };

  return { category: category.region };
};

export const getCategoryHeading = (
  t: TFunction,
  category: CustomizationCategory | null,
) => {
  if (category?.type === 'paint') {
    return t('customize.categoryHeading.paint');
  }
  if (category?.type === 'material') {
    return t(`customize.categoryHeading.${category.region}`);
  }

  return t('customize.categoryHeading.empty');
};

/**
 * Returns a menu for an image aspect with no configuration or filtering applied
 */
const getAspectCustomizeMenu = (t: TFunction, aspect: Aspect) => {
  const config = {
    interior: {
      cabinet: {
        label: t('customize.menu.cabinet'),
        icon: iconCabinet,
        category: {
          type: 'material',
          region: 'cabinet',
        },
        pictogram: pictogramCabinets,
      },
      floor: {
        label: t('customize.menu.floor'),
        icon: iconFloor,
        category: {
          type: 'material',
          region: 'floor',
        },
        pictogram: pictogramFlooring,
      },
    },
    exterior: {
      garage_door: {
        label: t('customize.menu.garage_door'),
        icon: iconGarage,
        category: {
          type: 'material',
          region: 'garage_door',
        },
        pictogram: pictogramGarageDoors,
      },
      roof: {
        label: t('customize.menu.roof'),
        icon: iconRoofline,
        category: {
          type: 'material',
          region: 'roof',
        },
        pictogram: pictogramRoofing,
      },
      walls: {
        label: t('customize.menu.walls'),
        icon: iconWall,
        category: {
          type: 'material',
          region: 'walls',
        },
        pictogram: pictogramWalls,
      },
      windows: {
        label: t('customize.menu.windows'),
        icon: iconWindow,
        category: {
          type: 'material',
          region: 'windows',
        },
        pictogram: pictogramWindows,
      },
    },
    any: {
      paint: {
        label: t('customize.menu.paint'),
        icon: iconPaint,
        category: {
          type: 'paint',
          region: 'walls',
        },
        pictogram: pictogramPaint,
      },
    },
  } satisfies Record<string, Record<string, MenuItem>>;

  switch (aspect) {
    case 'interior':
      return {
        ...config.interior,
        ...config.any,
      };
    default:
      return {
        ...config.exterior,
        ...config.any,
      };
  }
};

/**
 * Returns the customize menu. Applies partner configuration if a partner is
 * present.
 */
const usePartnerCustomizeMenu = (
  t: TFunction,
  aspect: Aspect,
): { menu: MenuItem[]; loading: boolean } => {
  const { data: categories, loading } = usePartnerCustomizeCategories();

  return useMemo(() => {
    const defaultMenu = getAspectCustomizeMenu(t, aspect);
    if (!categories) {
      return { menu: Object.values(defaultMenu), loading };
    }

    const builtMenuItems = new Map<string, MenuItem>();
    const menu: MenuItem[] = [];
    categories.forEach(category => {
      if (!category) return;

      const spaceType = category.aiPromptSpaceType;
      if (spaceType !== 'any' && spaceType !== aspect) return;

      const region = category.aiPromptRegion;

      if (!builtMenuItems.has(region)) {
        const menuItem = (defaultMenu as Record<string, MenuItem | undefined>)[
          region
        ];
        if (!menuItem) return;

        builtMenuItems.set(region, menuItem);
        menu.push(menuItem);
      }
    });
    return { menu, loading } as const;
  }, [aspect, categories, loading, t]);
};

/**
 * Creates a filter function that removes any region below the specified threshold.
 * If region data is not available, no filtering is applied
 */
const createDetectedRegionsFilter = (
  regionsFragment: AiGeneratedImageRegionsFragment | null | undefined,
  threshold: number,
) => {
  // TODO: Remove as unknown as Record<string, unknown>[] after JSON graphql schema type is updated
  const imageRegions = parseImageRegions(
    regionsFragment?.regions as unknown as Record<string, unknown>[],
  );

  const detectedRegionsMap = imageRegions.length
    ? regionMap(imageRegions)
    : undefined;

  return (region: string) => {
    if (!detectedRegionsMap) return true;
    const regionPercentage = detectedRegionsMap[region] ?? 0;
    return regionPercentage > threshold;
  };
};

/**
 * Creates a filter function that removes any menu items with a region below
 * the specified threshold. If region data is not available, no filtering is applied
 */
const createDetectedRegionsMenuFilter = (
  regionsFragment: AiGeneratedImageRegionsFragment | null | undefined,
  threshold: number,
) => {
  const detectedRegionsFilter = createDetectedRegionsFilter(
    regionsFragment,
    threshold,
  );
  return (menuItem: MenuItem) => {
    if (menuItem.category.type === 'paint') return true;
    return detectedRegionsFilter(menuItem.category.region);
  };
};

/**
 * Creates a filter function that removes any menu items with a category region
 * that has no available prompts.
 */
const createAvailableMaterialRegionsMenuFilter =
  (availableMaterialRegions: string[] | undefined) => (menuItem: MenuItem) =>
    menuItem.category.type === 'paint' ||
    !availableMaterialRegions ||
    availableMaterialRegions.length === 0 ||
    availableMaterialRegions.includes(menuItem.category.region);

/**
 * Creates a sort function that will bring to the front any menu item with
 * a region matching the brandRegion
 */
const createBrandRegionSort =
  (brandRegion: string | undefined) => (a: MenuItem, b: MenuItem) => {
    if (a.category.region === brandRegion) return -1;
    if (b.category.region === brandRegion) return 1;
    return 0;
  };

/**
 * Returns a list of regions that are above the ENABLED_REGION_THRESHOLD. If
 * region data is not available, no filtering is applied
 *
 * @param {SubRegion[]} regions - The full list of regions
 * @param {AiGeneratedImageRegionsFragment | undefined} regionsFragment - The regions detected in the image
 * @returns {SubRegion[]} - The array of enabled regions.
 */
export const getEnabledRegions = (
  regions: readonly SubRegion[],
  regionsFragment?: AiGeneratedImageRegionsFragment | null,
) =>
  regions.filter(
    createDetectedRegionsFilter(regionsFragment, ENABLED_REGION_THRESHOLD),
  );

/**
 * Returns a customize menu for a given image. Applies partner configuration to
 * the menu if a partner is present. Filters out any menu items that are not
 * applicable for the given image.
 *
 * @param t A translation function
 * @param image A display image
 * @param regionsFetchPolicy The fetch policy to use for region data. In some cases,
 * the region data in the cache may be stale. For contexts where accuracy of the regions is
 * critical, set this to 'network-only'. Defaults to 'cache-first'
 */
export const useCustomizeMenuForImage = (
  t: TFunction,
  image?: AiDisplayImageFragment | null,
  regionsFetchPolicy: 'network-only' | 'cache-first' = 'cache-first',
) => {
  const aspect = image ? getImageAspect(image) : 'exterior';
  const { menu, loading: partnerMenuLoading } = usePartnerCustomizeMenu(
    t,
    aspect,
  );
  const availableMaterialsRegions = useAvailableMaterialsRegions();

  const { data: regionsData, loading: regionsLoading } =
    useAiGeneratedImageRegionsQuery({
      skip: !image,
      variables: { id: String(image?.aiGeneratedImage.id) },
      fetchPolicy: regionsFetchPolicy,
    });

  const brandType = usePartnerCobranding()?.type;
  const loading = !image || regionsLoading || partnerMenuLoading;

  return useMemo(() => {
    const filteredMenu = menu
      .filter(
        createAvailableMaterialRegionsMenuFilter(availableMaterialsRegions),
      )
      .filter(
        createDetectedRegionsMenuFilter(
          regionsData?.aiGeneratedImage,
          ENABLED_REGION_THRESHOLD,
        ),
      )
      .sort(createBrandRegionSort(brandType));

    return { menu: filteredMenu, loading } as const;
  }, [availableMaterialsRegions, brandType, loading, menu, regionsData]);
};
